// src/pages/User/constants/userConstants.tsx - Enhanced with new features

import {
  FiCheckCircle,
  FiXCircle,
  FiTrash2,
  FiEdit,
  FiMinusCircle,
} from "react-icons/fi";
import {
  ChartBarIcon,
  UserIcon,
  StarIcon,
  OfficeBuildingIcon,
  CalendarIcon,
  ChatAlt2Icon,
} from "@heroicons/react/outline";

// TypeScript type definitions
export type ButtonVariant =
  | "success"
  | "danger"
  | "danger-text"
  | "danger-outline"
  | "info-outline"
  | "secondary"
  | "secondary-outline";

export type UserAction =
  | "accept"
  | "reject"
  | "block"
  | "unblock"
  | "delete"
  | "edit"
  | "analytics"
  | "profile"
  | "loyalty"
  | "branch"
  | "apptPref"
  | "commPref"
  | "editMessage";

export type UserStatus =
  | "Pending"
  | "Rejected"
  | "Blocked"
  | "Accepted"
  | "DeleteRequested"
  | "Unverified"
  | "AdminCreated"
  | "default";

export interface ButtonConfig {
  variant: ButtonVariant;
  action: UserAction;
  label: string;
  icon: JSX.Element;
}

export type UserButtonsConfig = {
  [K in UserStatus]: ButtonConfig[];
};

// Extended action buttons configuration for individual users
export const userButtonsConfig: UserButtonsConfig = {
  Pending: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
  ],
  Rejected: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
  ],
  Blocked: [
    {
      variant: "success",
      action: "unblock",
      label: "Unblock",
      icon: <FiCheckCircle />,
    },
  ],
  Accepted: [
    // New management buttons for accepted users
    {
      variant: "info-outline",
      action: "analytics",
      label: "Analytics",
      icon: <ChartBarIcon className="w-4 h-4" />,
    },
    {
      variant: "secondary-outline",
      action: "profile",
      label: "Profile",
      icon: <UserIcon className="w-4 h-4" />,
    },
    {
      variant: "secondary-outline",
      action: "loyalty",
      label: "Loyalty",
      icon: <StarIcon className="w-4 h-4" />,
    },
    {
      variant: "secondary-outline",
      action: "branch",
      label: "Branches",
      icon: <OfficeBuildingIcon className="w-4 h-4" />,
    },
    {
      variant: "danger-outline",
      action: "block",
      label: "Block",
      icon: <FiMinusCircle />,
    },
  ],
  DeleteRequested: [
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  Unverified: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  AdminCreated: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  default: [
    {
      variant: "info-outline",
      action: "analytics",
      label: "Analytics",
      icon: <ChartBarIcon className="w-4 h-4" />,
    },
    {
      variant: "secondary-outline",
      action: "profile",
      label: "Profile",
      icon: <UserIcon className="w-4 h-4" />,
    },
    {
      variant: "secondary-outline",
      action: "loyalty",
      label: "Loyalty",
      icon: <StarIcon className="w-4 h-4" />,
    },
    {
      variant: "secondary-outline",
      action: "branch",
      label: "Branches",
      icon: <OfficeBuildingIcon className="w-4 h-4" />,
    },
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
    {
      variant: "danger-outline",
      action: "block",
      label: "Block",
      icon: <FiMinusCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
    { variant: "secondary", action: "edit", label: "Edit", icon: <FiEdit /> },
  ],
};

// Bulk action buttons configuration (no individual management actions)
export const bulkUserButtonsConfig: UserButtonsConfig = {
  Pending: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  Rejected: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  Blocked: [
    {
      variant: "success",
      action: "unblock",
      label: "Unblock",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  Accepted: [
    {
      variant: "danger-outline",
      action: "block",
      label: "Block",
      icon: <FiMinusCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  DeleteRequested: [
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  Unverified: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  AdminCreated: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
  default: [
    {
      variant: "success",
      action: "accept",
      label: "Accept",
      icon: <FiCheckCircle />,
    },
    {
      variant: "danger",
      action: "reject",
      label: "Reject",
      icon: <FiXCircle />,
    },
    {
      variant: "danger-outline",
      action: "block",
      label: "Block",
      icon: <FiMinusCircle />,
    },
    {
      variant: "danger-text",
      action: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
    },
  ],
};

// Quick actions menu configuration (for dropdown/context menu)
interface QuickAction {
  action: UserAction;
  label: string;
  icon: JSX.Element;
  requiresPermission: string;
  condition?: (user: any) => boolean;
}

interface QuickActionGroup {
  group: string;
  actions: QuickAction[];
}

export const quickActionsConfig: QuickActionGroup[] = [
  {
    group: "Management",
    actions: [
      {
        action: "analytics",
        label: "View Analytics",
        icon: <ChartBarIcon className="w-4 h-4" />,
        requiresPermission: "view_analytics",
      },
      {
        action: "profile",
        label: "Customer Profile",
        icon: <UserIcon className="w-4 h-4" />,
        requiresPermission: "manage_customer",
      },
      {
        action: "loyalty",
        label: "Loyalty Management",
        icon: <StarIcon className="w-4 h-4" />,
        requiresPermission: "manage_loyalty",
      },
      {
        action: "branch",
        label: "Branch Management",
        icon: <OfficeBuildingIcon className="w-4 h-4" />,
        requiresPermission: "manage_branches",
      },
    ],
  },
  {
    group: "Preferences",
    actions: [
      {
        action: "apptPref",
        label: "Appointment Preferences",
        icon: <CalendarIcon className="w-4 h-4" />,
        requiresPermission: "manage_customer",
      },
      {
        action: "commPref",
        label: "Communication Preferences",
        icon: <ChatAlt2Icon className="w-4 h-4" />,
        requiresPermission: "manage_customer",
      },
    ],
  },
  {
    group: "Account",
    actions: [
      {
        action: "edit",
        label: "Edit Details",
        icon: <FiEdit />,
        requiresPermission: "edit_customer",
        condition: (user: any) => user.createdByAdmin,
      },
      {
        action: "editMessage",
        label: "Edit Admin Message",
        icon: <ChatAlt2Icon className="w-4 h-4" />,
        requiresPermission: "manage_customer",
        condition: (user: any) =>
          ["blocked", "rejected", "admin-created"].includes(user.status),
      },
    ],
  },
];

// Status variants for badges (unchanged)
export const userStatusVariants: Record<string, string> = {
  accepted: "success",
  rejected: "danger",
  pending: "warning",
  blocked: "secondary",
  "pending-deletion": "secondary",
  unverified: "warning",
  "admin-created": "info",
  default: "neutral",
};

// Display status mappings (unchanged)
export const statusMessages: Record<string, string> = {
  accepted: "Accepted",
  rejected: "Rejected",
  pending: "Pending",
  blocked: "Blocked",
  "pending-deletion": "Delete Requested",
  unverified: "Unverified",
  "admin-created": "Admin Created",
};

// User filter status mappings (unchanged)
export const userFilterStatus: Record<string, string> = {
  Accepted: "accepted",
  Rejected: "rejected",
  Blocked: "blocked",
  Pending: "pending",
  DeleteRequested: "pending-deletion",
  Unverified: "",
  AdminCreated: "admin-created",
  All: "",
};

// Helper function to get applicable actions for a user
export const getApplicableActions = (
  user: any,
  currentFilter: string,
  userPermissions: string[] = []
): ButtonConfig[] => {
  const config =
    userButtonsConfig[currentFilter as UserStatus] || userButtonsConfig.default;

  return config.filter((button: ButtonConfig) => {
    // Filter out edit button if user is not admin-created
    if (button.action === "edit" && !user.createdByAdmin) {
      return false;
    }

    // Add permission checks here if needed
    // if (button.requiresPermission && !userPermissions.includes(button.requiresPermission)) {
    //   return false;
    // }

    return true;
  });
};
